import React, { useEffect, useState } from 'react';
import { Grid, Paper, OutlinedInput, InputLabel, Button, Box, Typography, IconButton, Collapse, LinearProgress } from '@mui/material';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { dataGridLocaleConfig } from '../../../config/data-grid-locale';
import { usePoliticalPartySearchController } from './context/political-party-search-controller';
import { usePoliticalPartySearchState } from './context/political-party-search-context';
import PoliticalPartyAppService from '../../../application/platform/sge/political-party/political-party-app-service';
import { IPoliticalPartyPagedResponse } from '../../../model/sge/political-party/response/political-party-paged-response';

export function PoliticalPartySearchComponent() {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [rows, setRows] = useState<IPoliticalPartyPagedResponse[]>([]);
    const [loading, setLoading] = useState(false);

    const { handleOnChange, handleSearch, handleClearFilters, navigateToCreate, navigateToEdit } = usePoliticalPartySearchController();
    const { parameters, searchTrigger } = usePoliticalPartySearchState();

    useEffect(() => {
        setLoading(true);
        PoliticalPartyAppService.list(parameters)
            .then((result) => setRows(result.items))
            .finally(() => setLoading(false));
    }, [searchTrigger]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
        { field: 'abbreviation', headerName: 'Sigla', width: 120 },
        { field: 'isActive', headerName: 'Ativo', width: 80, type: 'boolean' },
        {
            field: 'actions', headerName: 'Ações', width: 80, sortable: false,
            renderCell: (params) => (
                <IconButton size="small" onClick={() => navigateToEdit(params.row.id)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Partidos Políticos"
                    subtitle="Gerencie os partidos políticos cadastrados"
                    breadcrumbs={[{ label: 'Partidos Políticos' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 1, p: 1, ml: -1, mr: -1, mb: filtersOpen ? 2 : 0, '&:hover': { backgroundColor: 'action.hover' } }}
                        onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterListIcon color="action" />
                            <Typography variant="h6">Filtros</Typography>
                        </Box>
                        <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                            {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={filtersOpen}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <InputLabel>Buscar por nome ou sigla</InputLabel>
                                <OutlinedInput fullWidth name="input-term" value={parameters.term || ''} onChange={handleOnChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={handleClearFilters}>Limpar</Button>
                                    <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>Consultar</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ position: 'relative', pb: '28px' }}>
                    <Paper sx={{ p: 2, height: '500px', display: 'flex', flexDirection: 'column' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            columnVisibilityModel={{ id: false }}
                            initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
                            sx={{ flex: 1 }}
                            disableRowSelectionOnClick
                            components={{ LoadingOverlay: LinearProgress }}
                            localeText={dataGridLocaleConfig}
                        />
                    </Paper>
                    <SpeedDial ariaLabel="Novo Partido" sx={{ position: 'absolute', bottom: 0, right: 16 }} onClick={navigateToCreate} icon={<SpeedDialIcon />} />
                </Box>
            </Grid>
        </React.Fragment>
    );
}
